<template>
  <WizardContent
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <BackButton
      v-show="showFullForm"
      class="mr-auto"
      @click="goBack"
    />
    <GateHeader>
      <template #logo>
        <JobNavIcon />
      </template>
      <template #page-title>
        Welcome back to JobNav!
      </template>
      <template #below-title>
        Don't have an account yet?
        <router-link
          to="signup"
        >
          Sign up now
        </router-link>
      </template>
    </GateHeader>
    <LandingAuth
      v-if="!emailChecked && !showFullForm"
      v-model="email"
      :trim-email="trimEmail"
      :loading="false"
      @submit-check="checkLoginMethod"
    />
    <div v-else class="w-100">
      <v-form
        ref="form"
        v-model="passwordFormvalid"
        class="form"
        @submit.prevent="submitForm"
      >
        <!-- data-testids below are used by Vanta/Uptrend transactions -->
        <DpplTextField
          v-model="email"
          label="Email"
          readonly
          data-testid="login-email-input"
          class="form-text-field"
        />
        <DpplTextField
          v-if="!singleSignOnMode"
          v-model="password"
          label="Password"
          :rules="passwordRules"
          type="password"
          class="form-text-field"
          autocomplete="off"
          data-testid="login-password-input"
          validate-on="blur"
          data-sentry-mask
        />
        <div class="w-100 text-right">
          <router-link
            class="text-button-2 text-linkDefault text-decoration-underline"
            to="password-reset"
          >
            Reset your password
          </router-link>
        </div>
        <DpplSignupFormButton
          :loading="loading"
          type="submit"
          data-testid="login-submit-button"
          class="mb-4 mt-4"
        >
          Continue
        </DpplSignupFormButton>
      </v-form>
      <section
        aria-label="Oauth and SSO logins"
      >
        <div class="signup-btns">
          <GoogleButton
            :google-url="googleUrl"
          />
          <MicrosoftButton
            :microsoft-url="microsoftUrl"
          />
          <SsoLoginButton
            v-if="ssoName || isGoogleSso || isMicrosoftSso"
            class="mb-6 w-100"
            :is-active="singleSignOnMode"
            :sso-provider="ssoProviderName"
            :sso-name="ssoName"
            :is-google="isGoogleSso"
            :is-microsoft="isMicrosoftSso"
            @toggle-sso="singleSignOnMode = !singleSignOnMode"
          />
        </div>
      </section>
    </div>
  </WizardContent>
</template>

<script>
import { nextTick } from 'vue';
import { mapActions } from 'pinia';
import WizardContent from '@/components/shared/WizardContent.vue';
import GateHeader from '@/components/shared/gates/GateHeader.vue';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import GoogleButton from '@/components/shared/gates/auth/GoogleButton.vue';
import MicrosoftButton from '@/components/shared/gates/auth/MicrosoftButton.vue';
import SsoLoginButton from '@/components/signup/SsoLoginButton.vue';
import LandingAuth from '@/components/shared/gates/auth/LandingAuth.vue';
import BackButton from '@/components/shared/gates/auth/BackButton.vue';
import { useWizard } from '@/stores/wizard/wizard';
import { useTracking } from '@/stores/tracking/tracking';
import { mixpanelIdentify } from '@/helpers/thirdParty/mixpanel';
import { isNotTestAccount } from '@/helpers/thirdParty/verifyTestAccount';
import { getLocalStorageObject } from '@/helpers/localStorage';
import {
  login,
  signupBuildAuthUrls,
  signupSendPasswordResetEmail,
  signupLoginMethod,
} from '@/api/signup';
import { redirectToSsoProvider } from '@/helpers/redirect';
import JobNavIcon from '@/assets/jobnav-payscale-icon-logo.svg?component';

export default {
  name: 'LoginView',
  components: {
    WizardContent,
    SsoLoginButton,
    DpplTextField,
    DpplSignupFormButton,
    GoogleButton,
    MicrosoftButton,
    GateHeader,
    JobNavIcon,
    LandingAuth,
    BackButton,
  },
  emits: ['track-page-load'],
  data() {
    return {
      email: '',
      password: '',
      emailRules: validation.emailRules(),
      emailChecked: false,
      passwordRules: validation.optionalPasswordRules(),
      lastAttemptEmail: null,
      passwordFormvalid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      singleSignOnMode: false,
      showFullForm: false,
      ssoProviderName: '',
      ssoName: '',
      isGoogleSso: false,
      isMicrosoftSso: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'JobNav',
      link: [{ rel: 'canonical', href: 'https://app.datapeople.io/' }],
      meta: [
        {
          name: 'description',
          property: 'og:description',
          content:
          'Log into JobNav begin writing job descriptions that are thoughtful, concise,'
          + ' and welcoming to candidates from every background.',
        },
        {
          name: 'robots',
          content: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          property: 'og:locale',
          content: 'en_US',
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          name: 'title',
          property: 'og:title',
          content: 'Log into your Account - JobNav',
        },
        {
          property: 'og:url',
          content: 'https://app.datapeople.io/',
        },
        {
          property: 'og:site_name',
          content: 'JobNav',
        },
        {
          property: 'og:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          property: 'og:image:secure_url',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '630',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:description',
          content:
          'Log into JobNav begin writing job descriptions that are thoughtful, concise,'
          + ' and welcoming to candidates from every background.',
        },
        {
          name: 'twitter:title',
          content: 'Log into your Account - JobNav',
        },
        {
          name: 'twitter:site',
          content: '@Datapeopleio',
        },
        {
          name: 'twitter:image',
          content: 'https://static.tapwage.com/taprecruit/Datapeople-fea.jpg',
        },
        {
          name: 'twitter:creator',
          content: '@Datapeopleio',
        },
      ],
    };
  },
  async created() {
    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls({ from: this.$route.query?.from });
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    Promise.all([getLinkedinAndGoogleOauthUrls()]).catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  mounted() {
    this.$emit('track-page-load', { page: 'Sign In' });

    const { passwordChanged } = this.$route.query;

    if (passwordChanged === 'true') {
      this.updateFlash({ status: 'success', message: 'Password changed successfully! Please log in with your new password.' });

      this.$router.replace({
        name: this.$route.name,
        query: { ...this.$route.query, passwordChanged: undefined },
      });
    }
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async checkLoginMethod() {
      if (!this.email) return;

      try {
        const result = await signupLoginMethod({ email: this.email });
        if (result?.success) {
          if (result.ssoMandatory) {
            const { from = '' } = this.$route.query;
            redirectToSsoProvider({
              from,
              provider: result.ssoName,
              isGoogle: result.loginMethod === 'google',
              isMicrosoft: result.loginMethod === 'microsoft',
            });
            return;
          }
          this.emailChecked = true;
          this.showFullForm = true;
          this.ssoProviderName = result.companyName;
          this.ssoName = result.ssoName;
          this.isGoogleSso = result.loginMethod === 'google';
          this.isMicrosoftSso = result.loginMethod === 'microsoft';
        }
      } catch (err) {
        this.updateFlash({ message: `Login method error: ${err.message}` });
        throw err;
      }
    },
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await login({
            email: this.email,
            password: this.password,
          });

          if (responseJSON.success === true) {
            const {
              mixpanel, user,
            } = responseJSON;

            /* Identify user for tracking purposes after they have logged in */
            mixpanelIdentify({ mixpanel });

            /* We must call this helper function because the tracking call does not have access
            to authData yet and cannot verify if a user is a test account */
            if (isNotTestAccount(user.userId)) {
              useTracking().login();
            }

            const redirectRoute = getLocalStorageObject({ key: 'redirectRoute', remove: true });
            const { from = '' } = this.$route.query;

            /* handle login from Datapeople Anywhere using username and password */
            if (from === 'datapeople-anywhere') {
              this.$router.replace({ name: 'datapeople-anywhere-login-success' });
            /* handle helpscout restricted docs auth callback */
            } else if (from.startsWith('help')) {
              const articlePath = from.substring(4);
              const searchParams = new URLSearchParams();
              searchParams.append('returnTo', articlePath);
              window.location = `/vue/helpscout-restricted-docs-callback?${searchParams}`;
            } else if (redirectRoute) {
              this.$router.replace(redirectRoute).catch(() => {});
            } else {
              this.$router.replace({ name: 'root' }).catch(() => {});
            }
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Login error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        const { success, email, errors } = responseJSON;
        if (success === false) {
          if (email) {
            this.lastAttemptEmail = email;
          }
          if (errors && errors.cooldown && errors.cooldown.length > 0) {
            this.updateFlash({ template: 'exceeded-tries' });
            nextTick(() => {
              document
                .getElementById('resend')
                .addEventListener('click', this.requestPasswordResetEmail);
            });
          } else if (errors && errors.password) {
            this.updateFlash({ template: 'incorrect-email-or-password' });
            nextTick(() => {
              document
                .getElementById('resend')
                .addEventListener('click', this.requestPasswordResetEmail);
            });
            // do not throw in this case as it's expected behavior and we don't need sentry error
            return;
          } else if (errors && errors.sso_mandatory) {
            const { from = '' } = this.$route.query;
            redirectToSsoProvider({
              from,
              provider: errors.provider,
              isGoogle: errors.is_google,
              isMicrosoft: errors.is_microsoft,
            });
            return;
          }
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
    async requestPasswordResetEmail() {
      try {
        const responseJSON = await signupSendPasswordResetEmail({
          email: this.lastAttemptEmail,
        });
        if (responseJSON.success === true) {
          this.updateFlash({ status: 'success', message: 'Password reset email sent!' });
        }
      } catch (e) {
        this.updateFlash({ message: e.message });
        throw e;
      } finally {
        this.loading = false;
      }
    },
    trimEmail() {
      this.email = validation.trimSpaces(this.email);
    },
    goBack() {
      this.emailChecked = false;
      this.showFullForm = false;
    },
  },
};
</script>

<style lang="less" scoped>
.wizard-wrapper {
  min-width: 330px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

:deep(.wizard-flash-message) {
  border-top-right-radius: 0px !important;
}

:deep(.slot-flash-message.full-width) {
  width: 450px;
}

.form {
  width: 100%;
}

.signup-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: 16px;
}

.sso-button:deep(.v-btn__content) {
  overflow: hidden;
  text-transform: initial;
  font-size: 16px;
}

.form-text-field :deep(.v-field .v-field__outline__start),
.form-text-field :deep(.v-field .v-field__outline__end) {
  border: 1px solid @grey-400;
}
</style>
